import { createFileRoute, Link, useMatch, useParams, useNavigate } from '@tanstack/react-router'
import { trpc } from '@/main'
import { useSessionUser } from '@/hooks/use-session'
import { Suspense, useState, useEffect, useRef, useDeferredValue } from 'react'
import { noop, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { PlusIcon } from 'lucide-react'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { clearProfileSelection, isProfileSelectionRetargeted, useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useHydrationSafePages } from '@/hooks/use-hydrating-render'
import { usePermissions } from '@/hooks/use-permissions'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SectionLabel } from '@/components/custom/section-label'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ListPanelBodySkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  LIST_PANE_WIDTH,
} from './-components/shared/layout'
import { AnimatedOutlet } from './-components/animated-outlet'
import {
  NavStack,
  NavStackStage,
  NavStackScreen,
  useStackedLayout,
} from '@/components/custom/route-transition/stack/nav-stack'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
  CollapsedPanelView,
  CollapsedPanelHeader,
  CollapsedPanelItems,
  CollapsedPanelLinkItem,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint } from './-components/shared/collapsible'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { EmployeeItem } from './-components/employees/employee-item'
import { PendingInvitationItem } from './-components/employees/pending-invitation-item'
import { ImportExportMenu } from './-components/employees/import-export-menu'
import { EmployeesCsvExport } from './-components/employees/employees-csv-export'
import { isLocalEnv } from '@/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { EmployeeForm } from './-components/companies/employee-form'
import { SelfAddGateDialog } from './-components/companies/self-add-gate-dialog'

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

export const Route = createFileRoute('/_app/employees')({
  context: () => ({
    employeeInvitationsQueryOptions: trpc.employees.getEmployeeInvitations.queryOptions(),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.employeeInvitationsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
  // Painted on the click frame; safe only because this match never re-keys — the search-keyed list lives on the
  // pathless `_list` layout below (see employees._list.tsx), so a keystroke cannot swap this rail for the skeleton.
})

function RouteComponent() {
  const { t } = useLingui()
  // Employees access is the `employees:read` permission (see usePermissions). The nav link is hidden for
  // anyone without it, but a direct URL hit would otherwise reach the panel's getEmployeesInfinite suspense
  // query and hard-crash on the backend's INSUFFICIENT_PERMISSIONS — so degrade to a no-access state here
  // (mirrors orgs.tsx).
  const canAccessEmployees = usePermissions().can('employees:read')

  return (
    <div id="report" className="@container flex h-full flex-col">
      {/* On a phone the two panes below are STACKED SCREENS, not a squashed row: the employees list, and the
          employee's report pushed in from the right over it (see nav-stack.tsx). The provider sits above
          PageLayout so the title bar can carry the stack's back chevron. */}
      <EmployeesStack>
        <PageLayout
          icon={NAVIGATION_ICONS.employees}
          title={t`Employees`}
          breadcrumbsSlot={canAccessEmployees ? <EmployeesBreadcrumbs /> : null}
        >
          {canAccessEmployees ? (
            <EmployeesLayout />
          ) : (
            <ScrollableContent>
              <ScrollableArea>
                <PanelEmptyState
                  icon={<NAVIGATION_ICONS.employees className="text-muted-foreground size-5" />}
                  title={t`No access`}
                  description={t`You don't have permission to access this section`}
                />
              </ScrollableArea>
            </ScrollableContent>
          )}
        </PageLayout>
      </EmployeesStack>
    </div>
  )
}

/** The employee-report URL: level 1 of the stack. */
const EMPLOYEE_DETAIL = /^\/employees\/[^/]+/

/**
 * Employees' mobile hierarchy — list → the employee's report. Two levels, not the standard three (a tapped
 * row goes STRAIGHT to the report; there is no members screen in between), so this declares its own ladder on
 * the engine's `NavStack` instead of using `SectionStack` (see the note at the top of section-stack.tsx).
 */
function EmployeesStack(props: { children: React.ReactNode }) {
  const canAccessEmployees = usePermissions().can('employees:read')
  const navigate = useNavigate()
  const pop = () => {
    void navigate({
      to: '/employees',
      search: (prev) => clearProfileSelection(prev),
    })
  }
  if (!canAccessEmployees) return <>{props.children}</>
  return (
    <NavStack levels={[true, EMPLOYEE_DETAIL]} onPop={pop}>
      {props.children}
    </NavStack>
  )
}

function EmployeesLayout() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  const hasEmployeeSelected = !!employeeId

  const employeeDialog = useSearchParamDialog('editEmployee')
  const editEmployeeId = typeof employeeDialog.value === 'number' ? employeeDialog.value : undefined

  // Admin self-add gate (Model 1): until the company has added ITSELF as an employee, ANY "+ Add" (it
  // navigates to editEmployee=new) opens the self-add gate instead of the normal new-employee form —
  // gating at the render site, so every trigger (toolbar, empty state, collapsed mini) is covered with
  // no bypass. The gate reads `getUser`, which the global post-write revalidation refreshes once the
  // self-add employee is created. That revalidation starts at mutation success — BEFORE the form's own
  // `onSubmit` closes the dialog, not after — so the ordering isn't guaranteed by construction; what
  // makes it safe is that `showSelfAddGate` is false for the whole self-add flow (`editEmployeeId` is
  // set once the form opens), so the refreshed `adminEmployeeId` has nothing to flip here.
  const user = useSessionUser()
  const showSelfAddGate = user?.type === 'company' && user.adminEmployeeId == null && editEmployeeId === undefined
  // Self-add is two steps: the gate captures the admin's email, then the full form opens prefilled with
  // it (admin-self mode). `selfAddEmail` holds the captured email between the two — null = still on the
  // gate. Reset on close so the next "+ Add" starts from the gate again.
  const [selfAddEmail, setSelfAddEmail] = useState<string | null>(null)
  const closeEmployeeDialog = (open: boolean) => {
    if (!open) setSelfAddEmail(null)
    employeeDialog.onOpenChange(open)
  }

  // No viewport-driven auto-collapse any more: the nav stack gives each pane the WHOLE screen and decides
  // which one is on it, so the provider stays expanded and the collapse is desktop's business only.
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      <Suspense fallback={null}>
        {employeeDialog.mounted &&
          (showSelfAddGate && selfAddEmail == null ? (
            <SelfAddGateDialog
              open={employeeDialog.open}
              onOpenChange={closeEmployeeDialog}
              // Captured a valid email: switch to the full form (admin-self mode) prefilled with it.
              onContinue={setSelfAddEmail}
            />
          ) : (
            <EmployeeForm
              employeeId={editEmployeeId}
              open={employeeDialog.open}
              onOpenChange={closeEmployeeDialog}
              adminSelf={showSelfAddGate}
              prefill={showSelfAddGate && selfAddEmail != null ? { email: selfAddEmail } : undefined}
            />
          ))}
      </Suspense>

      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Employees list. Always flex-1 on desktop, shrinks as the report grows. */}
        <NavStackScreen index={0} title={t`Employees`}>
          <StickyPanel className="flex-1 print:hidden">
            <CollapsiblePanel expandedWidth={LIST_PANE_WIDTH} disableTransition>
              <Suspense fallback={<ListPanelBodySkeleton />}>
                <EmployeesPanel />
              </Suspense>
            </CollapsiblePanel>
          </StickyPanel>
        </NavStackScreen>

        {/* LEVEL 1 — the report. Always in the DOM on desktop, width transitions from 0 to target.
            `ns-passthrough`: on mobile the slide-in group stops being a box so the level inside positions
            against the stage, not against this group's (0px / clipped) width. */}
        <SlideInPanel
          isOpen={hasEmployeeSelected}
          innerClassName="flex flex-col"
          className="ns-passthrough md:[--sip-left:450px]"
        >
          <NavStackScreen index={1} title={<EmployeeScreenTitle />}>
            <ScrollableArea>{hasEmployeeSelected && <AnimatedOutlet className="h-full" />}</ScrollableArea>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

/**
 * The title of the EMPLOYEE screen (nav stack level 1) — the employee's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getEmployee`, which the `$employeeId` layout's loader has
 * already prefetched, so it fills in with the rest of the screen.
 */
function EmployeeScreenTitle() {
  const { employeeId } = useParams({ strict: false })
  if (!employeeId) return null
  return (
    <Suspense fallback={null}>
      <EmployeeName />
    </Suspense>
  )
}

function EmployeeName() {
  // This renders in the LIST layout, above the `$employeeId` match, so it has no context of its own for
  // the employee — it reads the OWNING route's, which is matched exactly when `employeeId` is, and that is
  // the guard both call sites already apply.
  const { context } = useMatch({ from: '/_app/employees/_list/$employeeId' })
  const { data: employee } = useSuspenseQuery(context.employeeQueryOptions)
  return <>{employee?.employee_name ?? employee?.name ?? ''}</>
}

function EmployeesBreadcrumbs() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/employees"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {t`Employees`}
              </Link>
            }
          />
        </BreadcrumbItem>
        {employeeId && (
          <Suspense fallback={null}>
            <EmployeeDetailCrumb employeeId={employeeId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function EmployeeDetailCrumb(props: { employeeId: string }) {
  // The owning route's context — see EmployeeName.
  const { context } = useMatch({ from: '/_app/employees/_list/$employeeId' })
  const { data: employee } = useSuspenseQuery(context.employeeQueryOptions)
  const employeeName = employee?.employee_name ?? employee?.name
  if (!employeeName) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/employees/$employeeId/report"
              params={{ employeeId: props.employeeId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {employeeName}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function EmployeesPanel() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  const retargeted = isProfileSelectionRetargeted(useRootSearch())
  const navigate = useNavigate()
  const { context } = useMatch({ from: '/_app/employees/_list' })
  // The SAME gate the expanded panel's add control carries: an authorized employee views the company
  // read-only, and this collapsed-rail `+` was the one add affordance that shipped without it.
  const canAddEmployees = usePermissions().can('employees:write')

  // Defer the OPTIONS object the route built (reference-stable per match), so a search or type change
  // keeps the PREVIOUS list on screen while the new one loads instead of flashing the skeleton.
  const deferredEmployees = useDeferredValue(context.employeesInfiniteQueryOptions)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery(deferredEmployees)
  // True while the deferred options lag the live ones (a change is in flight) — keeps the fetching
  // indicator visible during the keep-previous transition.
  const isEmployeesFetching = isFetching || deferredEmployees !== context.employeesInfiniteQueryOptions

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  const allEmployees = useHydrationSafePages(data.pages).flatMap((page) => page.list)

  const addedEmployees = allEmployees.filter((e) => e.added)

  return (
    <>
      <CollapsedPanelView>
        <CollapsedPanelHeader>
          {canAddEmployees && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t`Add employee`}
              className="size-8"
              onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, editEmployee: 'new' }) })}
            >
              <PlusIcon className="size-4" />
            </Button>
          )}
        </CollapsedPanelHeader>
        <CollapsedPanelItems
          isLoading={false}
          isFetching={isEmployeesFetching}
          isFetchingMore={isFetchingNextPage}
          loadMoreRef={loadMoreRef}
        >
          {addedEmployees.map((employee) => {
            const hasSkills = employee.testPassed
            const hasValues = employee.testPassedValues
            const hasTraits = employee.testPassedPersonality

            return (
              <CollapsedPanelLinkItem
                key={employee.id}
                name={employee.employee_name ?? employee.name ?? ''}
                imageUrl={employee.image}
                type="employee"
                isSelected={!retargeted && employeeId === employee.id.toString()}
                tooltipContent={
                  <div className="flex flex-col gap-1.5">
                    <p className="font-medium">{employee.employee_name ?? employee.name}</p>
                    <div className="text-muted-foreground flex items-center gap-2.5 text-xs">
                      <CompletionIndicator skills={hasSkills} values={hasValues} traits={hasTraits} expanded />
                      {employee.job && <span>{employee.job}</span>}
                    </div>
                  </div>
                }
              >
                {(content) => (
                  <Link
                    to="/employees/$employeeId/report"
                    params={{ employeeId: employee.id.toString() }}
                    search={(prev) => ({
                      ...clearProfileSelection(prev),
                      first: { type: 'employee', id: employee.id },
                    })}
                  >
                    {content}
                  </Link>
                )}
              </CollapsedPanelLinkItem>
            )
          })}
        </CollapsedPanelItems>
      </CollapsedPanelView>

      <CollapsiblePanelContent>
        <EmployeesPanelContent />
      </CollapsiblePanelContent>
    </>
  )
}

function EmployeesPanelContent() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  const navigate = useNavigate()
  const rootSearch = useRootSearch()
  const { q: queryFromSearch, type } = rootSearch
  const retargeted = isProfileSelectionRetargeted(rootSearch)
  const { context } = useMatch({ from: '/_app/employees/_list' })
  const [localQuery, setLocalQuery] = useState(queryFromSearch)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  // All "+ Add" triggers just navigate to editEmployee=new; the render site decides whether to show the
  // self-add gate or the normal form (so the gate has no bypass).
  const openAddEmployee = () => navigate({ to: '.', search: (prev) => ({ ...prev, editEmployee: 'new' }) })
  // Authorized employees view the company read-only (Block C): hide the add control (the backend also
  // rejects the write). Companies + administrators can add.
  const canAddEmployees = usePermissions().can('employees:write')
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, and no row is ever marked
  // selected because selecting one leaves this list for another screen (see useStackedSelection).
  const stacked = useStackedLayout()

  const testTypeLabels: Record<string, string> = {
    competences: t`Competences`,
    values: t`Values`,
    personality: t`Personality`,
    global: t`Global`,
  }
  const currentTestLabel = testTypeLabels[type] ?? ''

  // Sync local query with the URL search param. Done DURING render via React's documented "adjusting
  // state when a prop changes" shape rather than in an effect: an effect commits and paints the stale
  // query for one frame first, so a back/forward visibly flashed the previous text in the box.
  const [prevQueryFromSearch, setPrevQueryFromSearch] = useState(queryFromSearch)
  if (prevQueryFromSearch !== queryFromSearch) {
    setPrevQueryFromSearch(queryFromSearch)
    setLocalQuery(queryFromSearch)
  }

  // Defer the OPTIONS object the route built (reference-stable per match), so a search or type change
  // keeps the PREVIOUS list on screen while the new one loads instead of flashing the skeleton.
  const deferredEmployees = useDeferredValue(context.employeesInfiniteQueryOptions)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery(deferredEmployees)
  // True while the deferred options lag the live ones (a change is in flight) — keeps the fetching
  // indicator visible during the keep-previous transition.
  const isEmployeesFetching = isFetching || deferredEmployees !== context.employeesInfiniteQueryOptions

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  const allEmployees = useHydrationSafePages(data.pages).flatMap((page) => page.list)

  // Consent requests still awaiting the person's answer (email matched an existing account).
  const { data: invitations } = useSuspenseQuery(Route.useRouteContext().employeeInvitationsQueryOptions)

  const totalCount = data.pages[0]?.pagination.count ?? 0

  // Derive "is a search active" from the DEFERRED `q` (the one that produced the list currently on
  // screen), NOT the live URL `q`. Otherwise clearing the search flips this to false while the stale
  // search results are still shown, briefly rendering the cold empty state before the full list loads.
  // Deferring it separately keeps it in lockstep with the deferred options above — same source, same
  // transition — without reaching into the options object for an input it no longer spells out.
  const isSearching = !!useDeferredValue(queryFromSearch)
  const showOnlyEmptyState = allEmployees.length === 0 && invitations.length === 0 && !isSearching

  const handleSearchChange = (value: string) => {
    setLocalQuery(value)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      navigate({ to: '.', search: (prev) => ({ ...prev, q: value || undefined }), replace: true })
    }, 300)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const pendingInvitationsSection = invitations.length > 0 && !isSearching && (
    <>
      <SectionLabel className="px-2">{t`Pending consent`}</SectionLabel>
      {invitations.map((invitation) => (
        <PendingInvitationItem key={invitation.id} invitation={invitation} />
      ))}
    </>
  )

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          <div className="flex h-23 items-center gap-2 border-b px-4 py-3">
            <SearchInput value={localQuery} onChange={handleSearchChange} placeholder={t`Search employees...`} />
            {/* Import & export is parked for now: local checkouts only (APP_ENV=local), so no deployed
                build shows it — danchiano.dev included, which runs APP_ENV=development. */}
            {canAddEmployees && isLocalEnv && <ImportExportMenu variant="sidebar" />}
            <EmployeesCsvExport />
            {/* Icon-only beside the download, for the same reason: the pane is 450px and the search box is
                what a reader types in. The tooltip and the `aria-label` carry the name. */}
            {canAddEmployees && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button variant="secondary" size="icon" onClick={openAddEmployee} aria-label={t`Add employee`}>
                      <PlusIcon className="size-4" />
                    </Button>
                  }
                />
                <TooltipContent side="bottom">{t`Add employee`}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </>
      )}

      <ScrollableList
        isFetching={isEmployeesFetching}
        loadMoreRef={loadMoreRef}
        isFetchingMore={isFetchingNextPage}
        isEmpty={allEmployees.length === 0 && invitations.length === 0}
        hint={
          allEmployees.length > 0 && (
            <SelectionHint visible={stacked || !employeeId} text={t`Select an employee to view their report`} />
          )
        }
        emptyState={
          <PanelEmptyState
            noResults={allEmployees.length === 0 && isSearching}
            icon={<NAVIGATION_ICONS.employees className="text-muted-foreground size-5" />}
            title={t`No employees yet`}
            description={t`Add employees to get started`}
            action={
              canAddEmployees ? (
                <Button variant="secondary" onClick={openAddEmployee}>
                  <PlusIcon className="size-4" />
                  {t`Add employee`}
                </Button>
              ) : undefined
            }
          />
        }
      >
        {pendingInvitationsSection}
        <SectionLabel className="px-2">
          {totalCount === 1 ? t`${totalCount} employee` : t`${totalCount} employees`}
        </SectionLabel>
        {allEmployees.map((employee) => {
          const isItemSelected = employeeId === employee.id.toString()
          const hasTest = employee.hasCurrentTest
          const isItemDisabled = !hasTest && !isItemSelected
          return (
            <EmployeeItem
              key={employee.id}
              employee={employee}
              // Never highlighted in a nav stack — see useStackedSelection — nor once the report is retargeted,
              // which is how the other sections' lists read (their row marker hangs off `second`). The disabled
              // computation above keeps the REAL selection so the open employee's row never flips disabled mid-pop.
              isSelected={!stacked && !retargeted && isItemSelected}
              hasCurrentTest={hasTest}
              currentTestLabel={currentTestLabel}
            >
              {(content) =>
                isItemDisabled ? (
                  <div>{content}</div>
                ) : (
                  <Link
                    to="/employees/$employeeId/report"
                    params={{ employeeId: employee.id.toString() }}
                    search={(prev) => ({
                      ...clearProfileSelection(prev),
                      first: { type: 'employee', id: employee.id },
                    })}
                  >
                    {content}
                  </Link>
                )
              }
            </EmployeeItem>
          )
        })}
      </ScrollableList>
    </>
  )
}
